/**
 * `GET/POST/PUT/DELETE venue/v1/manager/venues/{venueId}/courts[/{courtId}]` 계약 사본(#597 §1·§2·§7).
 * `venueProfileTypes.ts` 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한 필드를 파생·개명하지 않는다.
 */

import type { CourtPriceDayTypeGroup } from './priceTypes'

export type VenueCourtStatus = 'OPEN' | 'SUSPENDED' | 'MAINTENANCE'

export type VenueCourtFloorType =
  | 'WOOD'
  | 'RUBBER'
  | 'ARTIFICIAL_TURF'
  | 'NATURAL_TURF'
  | 'HARD'
  | 'CLAY'
  | 'CONCRETE'
  | 'ETC'

/** `#618` §3-2 — forward-only. 공공 구장(`public_venue_court`)과 값·라벨을 공유한다. */
export type VenueCourtType = 'INDOOR' | 'OUTDOOR' | 'MIXED'

export interface CourtOperatingDay {
  dayOfWeek: string
  dayOfWeekLabel: string
  startTime: string
  endTime: string
}

export interface CourtItem {
  courtId: number
  name: string
  /** 🔒 서버 채번(요청 필드 아님). 삭제해도 재채번하지 않는다. */
  displayOrder: number
  floorType: VenueCourtFloorType | null
  floorTypeLabel: string | null
  /** 실내/실외/혼합. 미입력이면 null(`#618` §3). */
  courtType: VenueCourtType | null
  courtTypeLabel: string | null
  /** 자유 문자열 규격 안내(예: "13.4m x 6.1m"). 🚫 예약·요금 계산에 쓰지 않는다. */
  sizeNote: string | null
  status: VenueCourtStatus
  statusLabel: string
  /** 사용자 예약 화면 노출 여부(`SUSPENDED` 만 false). */
  userVisible: boolean
  /** 새 예약을 받을 수 있는가(`SUSPENDED`·`MAINTENANCE` 는 false). */
  reservable: boolean
  reservationUnitMinutes: number
  /** 서버가 만든 문구 — 화면이 분을 문장으로 조립하지 않는다. */
  reservationUnitLabel: string
  maxReservationMinutes: number
  maxReservationLabel: string
  /** 🔒 관리자 표면에서는 **의미 불변 = 기본가**다(#852 §4-2 — 사용자 표면만 "최저 적용가"로 재정의됐다). */
  weekdayHourlyFee: number
  weekendHourlyFee: number
  /**
   * 시간대별 요금 구간이 하나라도 설정돼 있는가(#852 §4-2, 가법 추가).
   * 🚨 서버 배포 선후로 필드가 없을 수 있어 **옵셔널**로 받는다(설계 R4) — 없으면 지금과 동일하게 그린다.
   */
  hasPriceBands?: boolean
  /** 요일유형별 구간표(2행 고정). 상동 — 옵셔널로 받는다. */
  priceBands?: CourtPriceDayTypeGroup[]
  /** 운영시간 요약(요일별로 다르면 가장 이른 시작 ~ 가장 늦은 종료). 미설정이면 둘 다 null. */
  operatingStartTime: string | null
  operatingEndTime: string | null
  operatingHoursUniform: boolean
  /** 운영하는 요일만. 미설정이면 빈 배열. */
  operatingDays: CourtOperatingDay[]
  upcomingReservationCount: number
  /** `editable && upcomingReservationCount == 0`. */
  deletable: boolean
  /** 상동(예약 단위 필드 전용). */
  unitChangeable: boolean
  /** 막힌 것이 없으면 null. */
  blockNotice: string | null
  createdAt: string
  updatedAt: string | null
}

export interface CourtListView {
  /** 이 응답 모든 상태 판정의 기준시각. */
  baseTime: string
  venueId: string
  venueName: string
  /** 🔒 "종목"은 구장 단위다 — 코트에는 종목 컬럼이 없다. */
  facilityType: string
  facilityTypeLabel: string
  /** 지금 코트를 바꿀 수 있는가 = 쓰기 3EP 가 통과하는 조건과 같은 판정. */
  editable: boolean
  /** `editable=true` 면 null. 현재 forward-only 값 1종: `BOOKING_SYSTEM_PASS_INVALID`. */
  editBlockReason: string | null
  /** `editable=true` 면 null. 서버가 만든 문장. */
  editBlockNotice: string | null
  courtCount: number
  courts: CourtItem[]
}

/**
 * `POST`/`PUT` 요청 바디(#597 §2·§3, `courtType`·`sizeNote` 는 #618 §3). `PUT` 은 전량 교체다.
 * 🚨 `courtType`·`sizeNote` 는 "미전송=보존" 예외가 없다 — 매번 채워 보내지 않으면 기존 값이
 * 지워진다(#618 §3-1).
 */
export interface CourtSaveInput {
  name: string
  floorType: VenueCourtFloorType | null
  courtType: VenueCourtType | null
  sizeNote: string | null
  status: VenueCourtStatus
  reservationUnitMinutes: number
  maxReservationMinutes: number
  weekdayHourlyFee: number
  weekendHourlyFee: number
}

/** #597 §7 — forward-only. Figma 실측(코트 추가/상세 설정) 설명문을 그대로 쓴다. */
export const COURT_STATUS_CATALOG: { value: VenueCourtStatus; label: string; description: string }[] = [
  { value: 'OPEN', label: '운영 중', description: '예약을 받아요' },
  { value: 'SUSPENDED', label: '운영 중지', description: '예약 화면에서 숨겨져요' },
  { value: 'MAINTENANCE', label: '점검 중', description: '마감으로 표시돼요' },
]

/**
 * #597 §7 — forward-only. `GET` 응답은 "선택된 값"만 주고 "선택 가능한 전체 목록"은 안 주므로
 * 이 카탈로그를 선택 UI 값 목록으로 쓴다(`VENUE_AMENITY_CATALOG` 와 같은 이유).
 */
export const COURT_FLOOR_TYPE_CATALOG: { value: VenueCourtFloorType; label: string }[] = [
  { value: 'WOOD', label: '마루' },
  { value: 'RUBBER', label: '우레탄' },
  { value: 'ARTIFICIAL_TURF', label: '인조잔디' },
  { value: 'NATURAL_TURF', label: '천연잔디' },
  { value: 'HARD', label: '하드' },
  { value: 'CLAY', label: '클레이' },
  { value: 'CONCRETE', label: '콘크리트' },
  { value: 'ETC', label: '기타' },
]

/**
 * `#618` §3-2 — forward-only. `COURT_FLOOR_TYPE_CATALOG` 와 동일한 이유(선택 UI 값 목록).
 */
export const VENUE_COURT_TYPE_CATALOG: { value: VenueCourtType; label: string }[] = [
  { value: 'INDOOR', label: '실내' },
  { value: 'OUTDOOR', label: '실외' },
  { value: 'MIXED', label: '실내·실외' },
]

/** `#618` §1-4 — 규격 안내 문구 상한(서버 검증표 그대로). */
export const COURT_SIZE_NOTE_MAX_LENGTH = 50

/** #597 §5 — 30/60/90/120 외 값은 서버가 400 으로 거부하는 닫힌 집합(스키마 무변경 고지). */
export const RESERVATION_UNIT_OPTIONS: { value: number; label: string }[] = [
  { value: 30, label: '30분' },
  { value: 60, label: '1시간' },
  { value: 90, label: '1시간 30분' },
  { value: 120, label: '2시간' },
]
